import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Leaf, Flame, Layers, Sprout } from "lucide-react";
import { HERO_SLIDES, IMAGES } from "../mock";
import { scrollTo } from "./Navbar";

const ICONS = { Leaf, Flame, Layers, Sprout };
const STEP_VH = 0.6;
const FINAL_SLIDE_HOLD_VH = 1.4;

const useTypewriter = (text, speed = 55) => {
  const [out, setOut] = useState("");
  useEffect(() => {
    setOut("");
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) clearInterval(t);
    }, speed);
    return () => clearInterval(t);
  }, [text, speed]);
  return out;
};

const Hero = () => {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const slide = HERO_SLIDES[idx];
  const typed = useTypewriter(slide.line2);
  const n = HERO_SLIDES.length;

  useEffect(() => {
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const step = window.innerHeight * STEP_VH;
      const scrolled = -el.getBoundingClientRect().top;
      const next = Math.min(n - 1, Math.max(0, Math.floor(scrolled / step + 0.0001)));
      setIdx(next);
      const inHero = el.getBoundingClientRect().bottom > window.innerHeight * 0.5;
      window.dispatchEvent(new CustomEvent("arlan:phase", { detail: inHero ? HERO_SLIDES[next].tag : "" }));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [n]);

  const goTo = (i) => {
    const top = ref.current.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + i * window.innerHeight * STEP_VH + 2, behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} style={{ height: `calc(100vh + ${((n - 1) * STEP_VH + FINAL_SLIDE_HOLD_VH) * 100}vh)` }} className="relative bg-0" data-testid="hero-section" data-slide={slide.id}>
      <div className="sticky top-0 h-screen min-h-[600px] overflow-hidden">
        {HERO_SLIDES.map((s, i) => {
          const cls = `absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`;
          return s.video ? (
            <video key={s.id} className={cls} autoPlay muted loop playsInline preload="auto" aria-hidden="true" data-testid="hero-video">
              <source src={s.video} type="video/mp4" />
              <source src={s.video.replace(".mp4", ".webm")} type="video/webm" />
            </video>
          ) : (
            <img key={s.id} src={IMAGES[s.image]} alt="" className={cls} data-testid={`hero-background-${s.id}`} />
          );
        })}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.25) 50%, rgba(25,25,25,0.95))" }} />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-semibold text-white leading-[1.02] text-[3.2rem] sm:text-6xl lg:text-[6.5rem] tracking-tight" data-testid="hero-title">
            {slide.line1}{" "}
            <br />
            <span className="caret" data-testid="hero-typed">{typed}</span>
          </h1>
          <button onClick={() => scrollTo(slide.target)} className="btn-pill mt-10" data-testid="hero-cta">
            {slide.cta}
            <span className="circle"><ArrowRight className="w-4 h-4" /></span>
          </button>
        </div>

        <div className="absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-5" data-testid="hero-dots">
          {HERO_SLIDES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const active = i === idx;
            return (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                aria-label={s.line1}
                data-testid={`hero-dot-${s.id}`}
                className={`w-14 h-14 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  active ? "bg-black/80 border-black/80 scale-110" : "bg-black/20 border-white/15 hover:border-white/40"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.4} style={{ color: active ? "var(--green-light)" : "rgba(255,255,255,0.6)" }} />
              </button>
            );
          })}
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 md:hidden">
          {HERO_SLIDES.map((s, i) => (
            <button key={s.id} onClick={() => goTo(i)} aria-label={s.line1} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-3 bg-white/40"}`} data-testid={`hero-mobile-dot-${s.id}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
